import { Routes, Route, Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { AuthProvider } from './contexts/AuthContext'
import { LanguageProvider } from './contexts/LanguageContext'
import AuthGuard from './components/common/AuthGuard'
import AdminGuard from './components/common/AdminGuard'

// Pages
import Login from './pages/Login'
import HomePage from './pages/HomePage'
import Register from './pages/Register'
import ModeSelection from './pages/ModeSelection'
import ProjectSelection from './pages/ProjectSelection'
import DesignerPage from './pages/DesignerPage'
import EasyWayPage from './pages/EasyWayPage'
import VideoPage from './pages/VideoPage'
import AdminPage from './pages/AdminPage'
import PhotoToolsPage from './pages/PhotoToolsPage'
import PhotoComparePage from './pages/PhotoComparePage'
import PhotoFormatPage from './pages/PhotoFormatPage'
import PhotoFramePage from './pages/PhotoFramePage'
import PhotoCropPage from './pages/PhotoCropPage'
import PhotoCombinePage from './pages/PhotoCombinePage'
import PhotoMaskPage from './pages/PhotoMaskPage'
import PhotoLingerieFixPage from './pages/PhotoLingerieFixPage'
import PhotoAvatar3DPage from './pages/PhotoAvatar3DPage'
import NotFound from './pages/NotFound'
import StylistaPage from './pages/StylistaPage'
import MakeupPage from './pages/MakeupPage'
import HairdresserPage from './pages/HairdresserPage'
import DressingRoomPage from './pages/DressingRoomPage'
import CreditsPage from './pages/CreditsPage'
import StyleLibraryPage from './pages/StyleLibraryPage'

// Wersja aplikacji - zmienne ustawiane podczas buildu przez Vite
// UWAGA: Te wartości są generowane podczas 'npm run build', nie podczas uruchomienia aplikacji
// BUILD_TIMESTAMP zawiera timestamp release w formacie YYYY-MM-DD HH:MM:SS
declare const __APP_VERSION__: string
declare const __BUILD_TIMESTAMP__: string

const APP_VERSION = __APP_VERSION__
const BUILD_TIMESTAMP = __BUILD_TIMESTAMP__

function useRuntimeFooterLabel() {
  const [label, setLabel] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/api/version', { credentials: 'include' })
        if (!res.ok) return
        const data = await res.json()
        let l: string | null = data?.frontend?.footer_label || null
        if (!l && data?.frontend?.version) {
          const v = String(data.frontend.version)
          const bd = data?.frontend?.build_date
          l = bd ? `v${v} (build: ${bd})` : `v${v}`
        }
        if (!cancelled && l) setLabel(l)
      } catch {
        // ignore
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])
  return label
}

function App() {
  const runtimeFooter = useRuntimeFooterLabel()
  const appContent = (
    <Box sx={{ minHeight: '100vh', position: 'relative' }}>
      <LanguageProvider>
        <AuthProvider>
          <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          
          {/* Protected routes (require login) */}
          
          {/* Mode selection */}
          <Route path="/modes" element={
            <AuthGuard>
              <ModeSelection />
            </AuthGuard>
          } />
          
          {/* Project selection */}
          <Route path="/projects/:mode" element={
            <AuthGuard>
              <ProjectSelection />
            </AuthGuard>
          } />
          
          {/* Legacy route for backward compatibility */}
          <Route path="/projects" element={
            <AuthGuard>
              <Navigate to="/modes" replace />
            </AuthGuard>
          } />
          
          {/* Designer page */}
          <Route path="/designer/:projectId" element={
            <AuthGuard>
              <DesignerPage />
            </AuthGuard>
          } />

          {/* Foto Sesja (Photo Session) page */}
          <Route path="/easy-way/:projectName" element={
            <AuthGuard>
              <EasyWayPage />
            </AuthGuard>
          } />

          {/* Stylista page */}
          <Route path="/stylista/:projectName" element={
            <AuthGuard>
              <StylistaPage />
            </AuthGuard>
          } />

          {/* Makeup page */}
          <Route path="/makeup" element={
            <AuthGuard>
              <MakeupPage />
            </AuthGuard>
          } />

          {/* Hairdresser page */}
          <Route path="/hairdresser" element={
            <AuthGuard>
              <HairdresserPage />
            </AuthGuard>
          } />

          {/* Dressing Room page */}
          <Route path="/dressing-room" element={
            <AuthGuard>
              <DressingRoomPage />
            </AuthGuard>
          } />

          {/* Video page */}
          <Route path="/video" element={
            <AuthGuard>
              <VideoPage />
            </AuthGuard>
          } />

          {/* Photo Tools */}
          <Route path="/photo-tools" element={
            <AuthGuard>
              <PhotoToolsPage />
            </AuthGuard>
          } />
          <Route path="/photo-tools/compare" element={
            <AuthGuard>
              <PhotoComparePage />
            </AuthGuard>
          } />
          <Route path="/photo-tools/format" element={
            <AuthGuard>
              <PhotoFormatPage />
            </AuthGuard>
          } />
          <Route path="/photo-tools/frame" element={
            <AuthGuard>
              <PhotoFramePage />
            </AuthGuard>
          } />
          <Route path="/photo-tools/crop" element={
            <AuthGuard>
              <PhotoCropPage />
            </AuthGuard>
          } />
          <Route path="/photo-tools/combine" element={
            <AuthGuard>
              <PhotoCombinePage />
            </AuthGuard>
          } />
          <Route path="/photo-tools/maska" element={
            <AuthGuard>
              <PhotoMaskPage />
            </AuthGuard>
          } />
          <Route path="/photo-tools/lingerie-fix" element={
            <AuthGuard>
              <PhotoLingerieFixPage />
            </AuthGuard>
          } />
          <Route path="/photo-tools/avatar-3d" element={
            <AuthGuard>
              <PhotoAvatar3DPage />
            </AuthGuard>
          } />

          {/* Credits / payments */}
          <Route path="/credits" element={
            <AuthGuard>
              <CreditsPage />
            </AuthGuard>
          } />

          {/* Style Library */}
          <Route path="/style-library" element={
            <AuthGuard>
              <StyleLibraryPage />
            </AuthGuard>
          } />

          {/* Admin routes */}
          <Route path="/admin" element={
            <AdminGuard>
              <AdminPage />
            </AdminGuard>
          } />
          
          {/* 404 page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </AuthProvider>
      </LanguageProvider>
      
      {/* Informacja o wersji w prawym dolnym rogu */}
      <Typography 
        variant="caption" 
        sx={{ 
          position: 'fixed', 
          bottom: 5, 
          right: 10, 
          opacity: 0.7,
          backgroundColor: 'rgba(255,255,255,0.7)',
          padding: '2px 6px',
          borderRadius: 1,
          fontSize: '10px',
          zIndex: 1000
        }}
      >
        {runtimeFooter || `v${APP_VERSION} (build: ${BUILD_TIMESTAMP})`}
      </Typography>
    </Box>
  )

  return appContent
}

export default App
